// Paste to create a buyer — a two-phase modal. Phase 1 is a paste box that also
// harvests every hyperlink out of a rich clipboard payload; Extract runs the AI
// endpoint. Phase 2 is the EDITABLE confirm form, pre-filled from what the AI
// read, with the advisory duplicate warning.
//
// 🔴 The extraction is a PREFILL, never a create request. Nothing auto-creates:
// every field the extractor returns is rendered as an editable control, and the
// specialist's corrections are what `create` sends.

import { useState, type ClipboardEvent } from "react";
import { Save, Sparkles } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { Button, FormField, Input, Textarea, useToast } from "@/design-system/components/ui";
import { ApiError, extractErrorMessage } from "@/api/client";
import {
  buyersApi,
  type BuyerLeadExtractionResultDto,
  type BuyerLeadSource,
  type CreateBuyerLeadPayload,
} from "@/api/buyers";
import {
  AdvertisementPicker,
  useAdvertisementAttribution,
} from "@/components/buyers/AdvertisementPicker";
import { SpecialistCandidateSelect } from "@/components/buyers/SpecialistQuickPick";
import { BuyerTypeCreateFields } from "@/components/buyers/BuyerTypeCreateFields";
import {
  EMPTY_BUYER_TYPE_CREATE,
  buyerTypeCreatePayload,
  companyDetailsProblem,
  type BuyerTypeCreateState,
} from "@/components/buyers/buyerTypeCreate";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";
import { US_STATES } from "@/lib/usStates";
import { extractLinks, mergeLinks, type CapturedLink } from "@/lib/clipboardLinks";
import { BUYER_SOURCES, buyerLabel } from "./buyerFilters";
import {
  BuyerDuplicateWarning,
  assignmentNotice,
  useBuyerDuplicateCheck,
} from "./QuickAddBuyerModal";

interface PasteToCreateBuyerModalProps {
  onClose: () => void;
  /**
   * 🔴 <b>The id of the buyer this created.</b> Same contract as
   * {@link QuickAddBuyerModal} — it used to be called bare, so the pointer to
   * the record the whole paste-and-review flow exists to produce was discarded
   * the moment it arrived. `PasteToCreateListingModal` hands its created record
   * over for exactly this reason.
   */
  onCreated: (buyerId: string) => void;
}

/**
 * The review form. Money is held as a STRING so a half-typed figure survives a
 * keystroke — it is parsed once, on save.
 */
interface ReviewForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  city: string;
  state: string;
  zip: string;
  source: BuyerLeadSource;
  sourceDetail: string;
  liquidCapital: string;
  netWorth: string;
  maxPurchasePrice: string;
  downPaymentAvailable: string;
  targetIndustries: string;
  targetGeographies: string;
  targetRevenueMin: string;
  targetRevenueMax: string;
  targetCashFlowMin: string;
  timelineToPurchase: string;
  industryExperience: string;
  managementExperience: string;
  /** "" is "the paste did not say", which is NOT the same as "no". */
  isSbaCandidate: "" | "yes" | "no";
  financingNotes: string;
  relocationWillingness: string;
  notes: string;
  /** "Who is buying?" — prefilled only from a company reading the server
   *  checked against the paste; blank (an individual) otherwise. */
  buyerType: BuyerTypeCreateState;
}

const EMPTY_REVIEW: ReviewForm = {
  firstName: "", lastName: "", email: "", phone: "", city: "", state: "", zip: "",
  source: "Unknown", sourceDetail: "",
  liquidCapital: "", netWorth: "", maxPurchasePrice: "", downPaymentAvailable: "",
  targetIndustries: "", targetGeographies: "", targetRevenueMin: "",
  targetRevenueMax: "", targetCashFlowMin: "", timelineToPurchase: "",
  industryExperience: "", managementExperience: "", isSbaCandidate: "",
  financingNotes: "", relocationWillingness: "", notes: "",
  buyerType: EMPTY_BUYER_TYPE_CREATE,
};

const num = (v: number | null | undefined): string => (v == null ? "" : String(v));

/** Empty or unparseable is `null` — never `0`, which is a real answer. */
function toNumber(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t.replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
}

/**
 * `notesSummary` plus the captured link hrefs.
 *
 * <p>The hrefs also ride `capturedLinksJson` in their structured form. They are
 * repeated here on purpose: the notes are what a specialist actually reads on
 * the detail page, and a link only present in a JSON column nothing renders is
 * a link nobody sees.</p>
 */
function buildNotes(summary: string | null | undefined, links: CapturedLink[]): string {
  let notes = (summary ?? "").trim();
  if (links.length > 0) {
    const hrefs = Array.from(new Set(links.map((l) => l.href)));
    notes = (notes ? `${notes}\n\n` : "") + `Links:\n${hrefs.join("\n")}`;
  }
  return notes;
}

function fromExtraction(r: BuyerLeadExtractionResultDto, links: CapturedLink[]): ReviewForm {
  return {
    firstName: r.firstName ?? "",
    lastName: r.lastName ?? "",
    email: r.email ?? "",
    phone: r.phone ?? "",
    city: r.city ?? "",
    state: r.state ?? "",
    zip: r.zip ?? "",
    source: r.source,
    sourceDetail: r.sourceDetail ?? "",
    liquidCapital: num(r.liquidCapital),
    netWorth: num(r.netWorth),
    maxPurchasePrice: num(r.maxPurchasePrice),
    downPaymentAvailable: num(r.downPaymentAvailable),
    targetIndustries: r.targetIndustries ?? "",
    targetGeographies: r.targetGeographies ?? "",
    targetRevenueMin: num(r.targetRevenueMin),
    targetRevenueMax: num(r.targetRevenueMax),
    targetCashFlowMin: num(r.targetCashFlowMin),
    timelineToPurchase: r.timelineToPurchase ?? "",
    industryExperience: r.industryExperience ?? "",
    managementExperience: r.managementExperience ?? "",
    isSbaCandidate: r.isSbaCandidate == null ? "" : r.isSbaCandidate ? "yes" : "no",
    financingNotes: r.financingNotes ?? "",
    relocationWillingness: r.relocationWillingness ?? "",
    notes: buildNotes(r.notesSummary, links),
    buyerType: {
      buyerType: r.buyerType ?? "",
      companyName: r.acquiringCompanyName ?? "",
      title: r.acquiringCompanyTitle ?? "",
      website: r.acquiringCompanyWebsite ?? "",
    },
  };
}

export function PasteToCreateBuyerModal({ onClose, onCreated }: PasteToCreateBuyerModalProps) {
  // Phase 1 — paste.
  const [text, setText] = useState("");
  const [links, setLinks] = useState<CapturedLink[]>([]);
  /** Null on the 422 fallback path, where no model produced anything. */
  const [modelUsed, setModelUsed] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [fallbackNote, setFallbackNote] = useState<string | null>(null);

  // Phase 2 — review.
  const [reviewing, setReviewing] = useState(false);
  const [form, setForm] = useState<ReviewForm>(EMPTY_REVIEW);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showCompanyErrors, setShowCompanyErrors] = useState(false);

  const set = <K extends keyof ReviewForm>(key: K, val: ReviewForm[K]) =>
    setForm((f) => ({ ...f, [key]: val }));

  const toast = useToast();
  // The extraction's own Source is a prefill, not a hand-picked answer, so it
  // does NOT count as touched: an advertisement picked on the review step
  // still defaults Source from its channel unless the reviewer changed Source
  // themselves first.
  const attribution = useAdvertisementAttribution((s) => set("source", s));
  // Null = the queue (the default). Asked on the review step, where the
  // payload is assembled, so it sits beside the extraction it applies to.
  const [assignTo, setAssignTo] = useState<string | null>(null);

  const matches = useBuyerDuplicateCheck(
    reviewing ? form.email : "",
    reviewing ? form.phone : "",
  );

  const emailError = emailFieldError(form.email, { required: false });
  const phoneError = phoneFieldError(form.phone, { required: false });
  const hasName = !!form.firstName.trim() || !!form.lastName.trim();
  const canSave = hasName && !emailError && !phoneError;

  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const found = extractLinks(
      e.clipboardData.getData("text/html"),
      e.clipboardData.getData("text/plain"),
    );
    if (found.length > 0) setLinks((prev) => mergeLinks(prev, found));
  };

  const extract = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setExtracting(true);
    setExtractError(null);
    setFallbackNote(null);
    try {
      const result = await buyersApi.pasteExtract(trimmed);
      // Kept so the create can stamp WHICH model read the text. Paired with
      // `rawPastedText`: the two together are what make a mis-extraction
      // diagnosable rather than just visible.
      setModelUsed(result.modelUsed ?? null);
      setForm(fromExtraction(result, links));
      setReviewing(true);
    } catch (err) {
      // A 422 is raised BOTH when the text could not be read AND when the daily
      // AI cost ceiling is hit — those read completely differently to the person
      // waiting on it, so render the SERVER's message and only fall back to
      // fixed copy when it sent none. Either way the reviewer lands in an empty
      // form with their paste kept, rather than back at square one.
      if (err instanceof ApiError && err.status === 422) {
        setForm({ ...EMPTY_REVIEW, notes: buildNotes(null, links) });
        setFallbackNote(
          err.message.trim() ||
            "We couldn't read that automatically — add the details below; your pasted text is kept.",
        );
        setReviewing(true);
      } else {
        setExtractError(extractErrorMessage(err));
      }
    } finally {
      setExtracting(false);
    }
  };

  const save = async () => {
    setSaveError(null);
    if (!hasName) {
      setSaveError("Enter at least a first or last name.");
      return;
    }
    if (emailError || phoneError) {
      setSaveError(emailError ?? phoneError ?? null);
      return;
    }
    // The server refuses the same thing in the same words.
    const companyProblem = companyDetailsProblem(
      form.buyerType.buyerType,
      form.buyerType.companyName,
      form.buyerType.title,
    );
    if (companyProblem) {
      setShowCompanyErrors(true);
      setSaveError(companyProblem);
      return;
    }
    setSaving(true);
    try {
      const payload: CreateBuyerLeadPayload = {
        firstName: form.firstName.trim() || null,
        lastName: form.lastName.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        city: form.city.trim() || null,
        state: form.state || null,
        zip: form.zip.trim() || null,
        source: form.source,
        sourceDetail: form.sourceDetail.trim() || null,
        // Blank type adds no key at all; see `buyerTypeCreatePayload`.
        ...buyerTypeCreatePayload(form.buyerType),
        // Omitted rather than sent null: no pick is no attribution, and no
        // assignee lands the buyer unassigned in the queue.
        ...(attribution.advertisementId ? { advertisementId: attribution.advertisementId } : {}),
        ...(assignTo ? { assignToSpecialistUserId: assignTo } : {}),
        liquidCapital: toNumber(form.liquidCapital),
        netWorth: toNumber(form.netWorth),
        maxPurchasePrice: toNumber(form.maxPurchasePrice),
        downPaymentAvailable: toNumber(form.downPaymentAvailable),
        targetIndustries: form.targetIndustries.trim() || null,
        targetGeographies: form.targetGeographies.trim() || null,
        targetRevenueMin: toNumber(form.targetRevenueMin),
        targetRevenueMax: toNumber(form.targetRevenueMax),
        targetCashFlowMin: toNumber(form.targetCashFlowMin),
        timelineToPurchase: form.timelineToPurchase.trim() || null,
        industryExperience: form.industryExperience.trim() || null,
        managementExperience: form.managementExperience.trim() || null,
        isSbaCandidate: form.isSbaCandidate === "" ? null : form.isSbaCandidate === "yes",
        financingNotes: form.financingNotes.trim() || null,
        relocationWillingness: form.relocationWillingness.trim() || null,
        notes: form.notes.trim() || null,

        // 🔴 Provenance. `BuyerLeadDetailDto` reads the original paste back
        // precisely so a mis-extraction is recoverable by a human looking at
        // what the model was given — send them and that recovery path works;
        // drop them and the record silently becomes the model's summary with
        // no way back to the source. `aiModel` never travels without the text.
        rawPastedText: text.trim() || null,
        aiModel: modelUsed,
        capturedLinksJson: links.length > 0 ? JSON.stringify(links) : null,

        // Records that a human SAW the duplicate warning and continued. The
        // rule here is detect, warn, never block, so this acknowledgement is
        // the only thing distinguishing a considered decision from an
        // unnoticed collision. Sent truthfully — true only when matches were
        // actually on screen.
        confirmCreateDespiteDuplicate: matches.length > 0,
      };
      const created = await buyersApi.create(payload);
      const notice = assignmentNotice(created);
      if (notice) toast.info(notice);
      onCreated(created.id);
      onClose();
    } catch (err) {
      setSaveError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={reviewing ? "Review & save buyer" : "Paste to create a buyer"}
      description={
        reviewing
          ? "Everything below is editable — correct anything the extraction got wrong before saving."
          : undefined
      }
      size="md"
      footer={
        <>
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          {!reviewing ? (
            <Button
              size="sm"
              disabled={extracting || text.trim().length === 0}
              onClick={() => void extract()}
            >
              <Sparkles size={14} strokeWidth={1.5} aria-hidden />{" "}
              {extracting ? "Extracting…" : "Extract"}
            </Button>
          ) : (
            <Button size="sm" disabled={saving || !canSave} onClick={() => void save()}>
              {saving ? (
                "Saving…"
              ) : (
                <>
                  <Save size={14} strokeWidth={1.5} aria-hidden />{" "}
                  {matches.length > 0 ? "Save anyway" : "Save"}
                </>
              )}
            </Button>
          )}
        </>
      }
    >
      {!reviewing ? (
        <div className="grid gap-space-3">
          <p className="text-small text-gray-mid m-0">
            Paste an inquiry, an email, or a message thread — we&apos;ll pull out the
            buyer&apos;s details for you to review before anything is saved. Links are
            captured automatically.
          </p>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onPaste={handlePaste}
            rows={9}
            placeholder="Paste here…"
            aria-label="Paste text to extract a buyer from"
            autoFocus
          />
          {links.length > 0 && (
            <p className="text-caption text-gray-mid m-0">
              Captured {links.length} link{links.length === 1 ? "" : "s"} — they&apos;ll be
              added to the notes.
            </p>
          )}
          {extractError && (
            <p className="text-caption text-status-error m-0" role="alert">
              {extractError}
            </p>
          )}
        </div>
      ) : (
        <div className="grid gap-space-3">
          {fallbackNote && (
            <div
              className="rounded-md border border-status-info bg-white-warm px-space-3 py-space-2 text-caption text-onyx"
              role="status"
            >
              {fallbackNote}
            </div>
          )}

          <BuyerDuplicateWarning matches={matches} />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <FormField label="First name">
              <Input value={form.firstName} autoFocus onChange={(e) => set("firstName", e.target.value)} />
            </FormField>
            <FormField label="Last name">
              <Input value={form.lastName} onChange={(e) => set("lastName", e.target.value)} />
            </FormField>
            <FormField label="Email" error={emailError}>
              <Input type="email" value={form.email} invalid={!!emailError} onChange={(e) => set("email", e.target.value)} />
            </FormField>
            <FormField label="Phone" error={phoneError}>
              <Input type="tel" value={form.phone} invalid={!!phoneError} onChange={(e) => set("phone", e.target.value)} />
            </FormField>
            <BuyerTypeCreateFields
              value={form.buyerType}
              onChange={(next) => set("buyerType", next)}
              showErrors={showCompanyErrors}
            />
            <FormField label="City">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
            </FormField>
            <FormField label="State">
              <Select value={form.state} onChange={(e) => set("state", e.target.value)}>
                <option value="">—</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </Select>
            </FormField>
            <FormField label="ZIP">
              <Input value={form.zip} onChange={(e) => set("zip", e.target.value)} />
            </FormField>
            <FormField label="Source">
              <Select
                value={form.source}
                onChange={(e) => attribution.setSourceByHand(e.target.value as BuyerLeadSource)}
              >
                {BUYER_SOURCES.map((s) => (
                  <option key={s} value={s}>{buyerLabel(s)}</option>
                ))}
              </Select>
            </FormField>
            <FormField label="Source detail" className="sm:col-span-2">
              <Input value={form.sourceDetail} onChange={(e) => set("sourceDetail", e.target.value)} />
            </FormField>
            <FormField
              label="Assign to"
              helperText="Leave unassigned and the buyer lands in the queue."
            >
              <SpecialistCandidateSelect buyerLeadId={null} value={assignTo} onChange={setAssignTo} />
            </FormField>
            <AdvertisementPicker
              value={attribution.advertisementId}
              onChange={attribution.pickAdvertisement}
            />
          </div>

          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0 mt-space-2">
            Capacity
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <FormField label="Liquid capital">
              <Input value={form.liquidCapital} onChange={(e) => set("liquidCapital", e.target.value)} />
            </FormField>
            <FormField label="Net worth">
              <Input value={form.netWorth} onChange={(e) => set("netWorth", e.target.value)} />
            </FormField>
            <FormField label="Max purchase price">
              <Input value={form.maxPurchasePrice} onChange={(e) => set("maxPurchasePrice", e.target.value)} />
            </FormField>
            <FormField label="Down payment available">
              <Input value={form.downPaymentAvailable} onChange={(e) => set("downPaymentAvailable", e.target.value)} />
            </FormField>
          </div>

          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0 mt-space-2">
            What they&apos;re looking for
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <FormField label="Target industries" className="sm:col-span-2">
              <Input value={form.targetIndustries} onChange={(e) => set("targetIndustries", e.target.value)} />
            </FormField>
            <FormField label="Target geographies" className="sm:col-span-2">
              <Input value={form.targetGeographies} onChange={(e) => set("targetGeographies", e.target.value)} />
            </FormField>
            <FormField label="Target revenue min">
              <Input value={form.targetRevenueMin} onChange={(e) => set("targetRevenueMin", e.target.value)} />
            </FormField>
            <FormField label="Target revenue max">
              <Input value={form.targetRevenueMax} onChange={(e) => set("targetRevenueMax", e.target.value)} />
            </FormField>
            <FormField label="Target cash flow min">
              <Input value={form.targetCashFlowMin} onChange={(e) => set("targetCashFlowMin", e.target.value)} />
            </FormField>
            <FormField label="Timeline to purchase">
              <Input value={form.timelineToPurchase} onChange={(e) => set("timelineToPurchase", e.target.value)} />
            </FormField>
          </div>

          <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow m-0 mt-space-2">
            Experience &amp; financing
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-3">
            <FormField label="Industry experience" className="sm:col-span-2">
              <Textarea rows={2} value={form.industryExperience} onChange={(e) => set("industryExperience", e.target.value)} />
            </FormField>
            <FormField label="Management experience" className="sm:col-span-2">
              <Textarea rows={2} value={form.managementExperience} onChange={(e) => set("managementExperience", e.target.value)} />
            </FormField>
            <FormField label="SBA candidate">
              <Select
                value={form.isSbaCandidate}
                onChange={(e) => set("isSbaCandidate", e.target.value as ReviewForm["isSbaCandidate"])}
              >
                <option value="">Not stated</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </Select>
            </FormField>
            <FormField label="Relocation willingness">
              <Input value={form.relocationWillingness} onChange={(e) => set("relocationWillingness", e.target.value)} />
            </FormField>
            <FormField label="Financing notes" className="sm:col-span-2">
              <Textarea rows={2} value={form.financingNotes} onChange={(e) => set("financingNotes", e.target.value)} />
            </FormField>
          </div>

          <FormField
            label="Notes"
            helperText="Everything in the paste that mapped to no field above, plus any captured links."
          >
            <Textarea rows={5} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </FormField>

          {saveError && (
            <p className="text-caption text-status-error m-0" role="alert">
              {saveError}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
